import React, { useCallback, useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { LocationNodeData } from '../../../../../@types/live-search.type';
import { ServiceNodeAllocationType } from '../../../../../@types/allocation.type';
import { useSearchAllocationNodes } from '../../../../../queries/live-search-query';

export interface AllocationDialogProps {
  open: boolean;
  onClose: () => void;
  currentTechnician: string;
  onSubmit: (nodeId: number, allocationTypeCode: string) => void;
  submitting: boolean;
}

export const AllocationDialog: React.FC<AllocationDialogProps> = ({
  open,
  onClose,
  currentTechnician,
  onSubmit,
  submitting,
}) => {
  const theme = useTheme();
  const searchMutation = useSearchAllocationNodes();
  const [selectedNode, setSelectedNode] = useState<LocationNodeData | null>(null);
  const [allocationType, setAllocationType] = useState(ServiceNodeAllocationType.Primary);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<LocationNodeData[]>([]);

  useEffect(() => {
    if (open) {
      setSelectedNode(null);
      setAllocationType(ServiceNodeAllocationType.Primary);
      setInputValue('');
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open || inputValue.length < 2) { setOptions([]); return; }
    const timer = setTimeout(() => {
      searchMutation.mutateAsync(inputValue).then(setOptions).catch(() => setOptions([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue, open]);

  const handleSubmit = useCallback(() => {
    if (selectedNode) onSubmit(selectedNode.id, allocationType);
  }, [selectedNode, allocationType, onSubmit]);

  const optionSx = (active: boolean) => ({
    px: 1.5, py: 1, borderRadius: 1, cursor: 'pointer', textAlign: 'center' as const,
    border: '1px solid', fontSize: '12px', fontWeight: 500,
    borderColor: active ? theme.palette.v4.accent : theme.palette.v4.border,
    bgcolor: active ? theme.palette.v4.accent : 'transparent',
    color: active ? '#fff' : theme.palette.text.secondary,
    '&:hover': { borderColor: theme.palette.v4.accent, opacity: 0.85 },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>Allocate Technician</DialogTitle>
      <DialogContent>
        {currentTechnician && (
          <Typography sx={{ fontSize: '12px', color: 'text.secondary', mb: 2 }}>
            Current: <strong>{currentTechnician}</strong>
          </Typography>
        )}
        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
          Allocation Type
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Box sx={optionSx(allocationType === ServiceNodeAllocationType.Primary)} onClick={() => setAllocationType(ServiceNodeAllocationType.Primary)}>
            Primary
          </Box>
          <Box sx={optionSx(allocationType === ServiceNodeAllocationType.Secondary)} onClick={() => setAllocationType(ServiceNodeAllocationType.Secondary)}>
            Secondary
          </Box>
        </Box>
        <Autocomplete
          size="small"
          options={options}
          getOptionLabel={(opt) => opt.name}
          filterOptions={(x) => x}
          inputValue={inputValue}
          onInputChange={(_, val) => setInputValue(val)}
          onChange={(_, node) => setSelectedNode(node)}
          loading={searchMutation.isPending}
          renderInput={(params) => (
            <TextField {...params} label="Search technician / depot" autoFocus
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={`${option.id}-${option.code}`}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{option.name}</Typography>
                <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>{option.nodeTypeName} &middot; {option.code}</Typography>
              </Box>
            </li>
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selectedNode || submitting}
          sx={{ textTransform: 'none', bgcolor: 'v4.accent', '&:hover': { bgcolor: 'v4.accent', opacity: 0.9 } }}
        >
          {submitting ? <CircularProgress size={16} sx={{ color: 'common.white' }} /> : 'Allocate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
